import { useCallback } from 'react';

import { InterstitialFlowTypesEnum } from '@eluve/graphql-types';

import {
  markInterstitialAsCompleted,
  markInterstitialAsSkipped,
} from '../utils/sessionStorage';

import { useGenericFlowMutations } from './useGenericFlowMutations';
import { useInterstitials } from './useInterstitials';

/**
 * Hook replacing the previous HOC pattern. Provides complete/skip actions
 * for a specific interstitial flow. Business logic components call these
 * after performing their own domain-specific mutations.
 */
export const useInterstitialFlow = (flowName: InterstitialFlowTypesEnum) => {
  const { context, setUserRegistrationShouldShow } = useInterstitials();
  if (!context) {
    throw new Error(
      'useInterstitialFlow must be used when context is available',
    );
  }

  const { completeFlow, skipFlow } = useGenericFlowMutations({ flowName });

  const complete = useCallback(
    async (data?: unknown) => {
      markInterstitialAsCompleted(flowName);
      await completeFlow(data);
      if (flowName === 'USER_REGISTRATION') {
        setUserRegistrationShouldShow(false);
      }
    },
    [flowName, completeFlow, setUserRegistrationShouldShow],
  );

  const skip = useCallback(async () => {
    markInterstitialAsSkipped(flowName);
    await skipFlow();
    if (flowName === 'USER_REGISTRATION') {
      setUserRegistrationShouldShow(false);
    }
  }, [flowName, skipFlow, setUserRegistrationShouldShow]);

  return { complete, skip };
};
